import React, { useEffect, useMemo, useRef, useState } from "react";
import { CircleAlert, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import equipmentService from "@/services/equipmentService";
import {
  NAME_MAX,
  formatUnits,
  getStatusKey,
  getUsage,
  getUsed,
  STATUS,
  validateStorageLocation,
} from "./storageLocationUtils";

// No celular o diálogo vira "bottom sheet" (preso embaixo, entra de baixo)
const SHEET_CLASSES =
  "max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:left-0 max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-b-none max-sm:rounded-t-3xl max-sm:border-x-0 max-sm:border-b-0 max-sm:pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] max-sm:data-[state=open]:slide-in-from-left-0 max-sm:data-[state=open]:slide-in-from-bottom-full max-sm:data-[state=closed]:slide-out-to-left-0 max-sm:data-[state=closed]:slide-out-to-bottom-full max-sm:data-[state=open]:zoom-in-100 max-sm:data-[state=closed]:zoom-out-100";

const inputClass =
  "h-11 w-full rounded-xl border bg-card px-3.5 text-base text-foreground transition-colors placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-[3px] focus:ring-brand-pistachio/25 disabled:opacity-60 md:text-sm";

const EMPTY = { name: "", equipment_id: "", capacity: "" };

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p id={id} className="mt-1 text-xs font-medium text-destructive" role="alert">
      {message}
    </p>
  );
}

function FieldHint({ id, error, children }) {
  return error ? (
    <FieldError id={id} message={error} />
  ) : (
    <p id={id} className="mt-1 text-[11px] text-muted-foreground">
      {children}
    </p>
  );
}

/**
 * Criar/editar local de armazenamento. `storageLocation` null = criação.
 * `storageLocations` (lista completa) serve para barrar nome repetido no
 * mesmo equipamento.
 */
function StorageLocationFormModal({ open, onOpenChange, storageLocation, storageLocations = [], onSave, loading }) {
  const [formData, setFormData] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [equipments, setEquipments] = useState([]);
  const [loadingEquipments, setLoadingEquipments] = useState(false);
  const nameRef = useRef(null);
  const isEdit = Boolean(storageLocation);

  // Equipamentos atualizados a cada abertura (podem ter sido cadastrados em outra tela)
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoadingEquipments(true);
    equipmentService
      .getAllEquipments()
      .then((data) => active && setEquipments(Array.isArray(data) ? data : []))
      .catch(() => active && setEquipments([]))
      .finally(() => active && setLoadingEquipments(false));
    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setFormData(
      storageLocation
        ? {
            name: storageLocation.name ?? "",
            equipment_id: String(storageLocation.equipment_id ?? ""),
            capacity: storageLocation.capacity ? String(storageLocation.capacity) : "",
          }
        : EMPTY
    );
  }, [storageLocation, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;
    const others = storageLocations.filter((l) => String(l.id) !== String(storageLocation?.id));
    const { errors: nextErrors, payload } = validateStorageLocation(
      { ...formData, description: storageLocation?.description },
      others
    );
    setErrors(nextErrors);
    if (!payload) {
      if (nextErrors.name) nameRef.current?.focus();
      return;
    }
    onSave(payload);
  };

  const equipmentOptions = useMemo(() => {
    const options = equipments.map((eq) => ({ value: String(eq.id), label: eq.name || `#${eq.id}` }));
    // Garante que o equipamento atual apareça mesmo se a lista falhar
    const currentId = String(storageLocation?.equipment_id ?? "");
    if (open && currentId && !options.some((o) => o.value === currentId)) {
      options.unshift({ value: currentId, label: storageLocation.equipment_name || `#${currentId}` });
    }
    return options;
  }, [equipments, storageLocation, open]);

  // Indicador em tempo real: ocupação atual com a capacidade digitada
  const used = isEdit ? getUsed(storageLocation) : 0;
  const typedCapacity = /^\d+$/.test(formData.capacity.trim()) ? Number(formData.capacity) : 0;
  const previewUsage = getUsage(typedCapacity, used);
  const previewStatus = STATUS[getStatusKey(previewUsage)];
  const showPreview = isEdit && previewUsage !== null;

  return (
    <Dialog open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
      <DialogContent
        className={cn("max-h-[90vh] max-w-md gap-0 overflow-y-auto rounded-2xl bg-background p-6", SHEET_CLASSES)}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          nameRef.current?.focus();
        }}
      >
        <div className="mx-auto -mt-2 mb-4 h-1.5 w-12 rounded-full bg-border sm:hidden" aria-hidden="true" />
        <DialogHeader className="space-y-0.5 pb-4 pr-8 text-left">
          <DialogTitle className="font-display text-lg font-bold tracking-tight text-foreground">
            {isEdit ? "Editar Local de Armazenamento" : "Novo Local de Armazenamento"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isEdit
              ? "Atualize as informações do local de armazenamento."
              : "Preencha os dados para criar um novo local de armazenamento."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div>
            <Label htmlFor="storage-name" className="mb-1 block text-xs font-semibold">
              Nome <span className="text-destructive">*</span>
            </Label>
            <input
              id="storage-name"
              ref={nameRef}
              value={formData.name}
              maxLength={NAME_MAX}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Ex.: Prateleira A1, Bandeja I"
              autoComplete="off"
              disabled={loading}
              aria-invalid={Boolean(errors.name) || undefined}
              aria-describedby={errors.name ? "storage-name-error" : undefined}
              className={cn(inputClass, "font-medium", errors.name ? "border-destructive" : "border-border")}
            />
            <FieldError id="storage-name-error" message={errors.name} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="storage-equipment" className="mb-1 block text-xs font-semibold">
                Equipamento <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.equipment_id}
                onValueChange={(value) => handleChange("equipment_id", value)}
                disabled={loading || loadingEquipments || equipmentOptions.length === 0}
              >
                <SelectTrigger
                  id="storage-equipment"
                  aria-invalid={Boolean(errors.equipment_id) || undefined}
                  aria-describedby="storage-equipment-hint"
                  className={cn(
                    "h-11 rounded-xl bg-card px-3.5 text-base focus:ring-[3px] focus:ring-brand-pistachio/25 md:text-sm",
                    errors.equipment_id ? "border-destructive" : "border-border"
                  )}
                >
                  <SelectValue
                    placeholder={
                      loadingEquipments
                        ? "Carregando..."
                        : equipmentOptions.length === 0
                        ? "Nenhum cadastrado"
                        : "Selecione"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {equipmentOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldHint id="storage-equipment-hint" error={errors.equipment_id}>
                {!loadingEquipments && equipmentOptions.length === 0
                  ? "Cadastre um equipamento antes de criar o local."
                  : "Equipamento vinculado"}
              </FieldHint>
            </div>

            <div>
              <Label htmlFor="storage-capacity" className="mb-1 block text-xs font-semibold">
                Capacidade <span className="text-destructive">*</span>
              </Label>
              <input
                id="storage-capacity"
                inputMode="numeric"
                value={formData.capacity}
                onChange={(e) => handleChange("capacity", e.target.value.replace(/\D/g, ""))}
                placeholder="100"
                disabled={loading}
                aria-invalid={Boolean(errors.capacity) || undefined}
                aria-describedby="storage-capacity-hint"
                className={cn(inputClass, "font-semibold", errors.capacity ? "border-destructive" : "border-border")}
              />
              <FieldHint id="storage-capacity-hint" error={errors.capacity}>
                Capacidade total em unidades
              </FieldHint>
            </div>
          </div>

          {showPreview && (
            <div
              className={cn(
                "space-y-1.5 rounded-xl p-3",
                previewUsage >= 100 ? "bg-destructive/5" : "bg-surface-low"
              )}
              aria-live="polite"
            >
              <div className={cn("flex items-center justify-between text-[13px] font-semibold", previewStatus.text)}>
                <span className="flex items-center gap-1">
                  <CircleAlert className="h-4 w-4" />
                  Status atual: {previewUsage > 100 ? "Sobrecarga" : previewStatus.label}
                </span>
                <span className="font-bold">{previewUsage}%</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-high">
                <div
                  className={cn("h-full rounded-full transition-all duration-300", previewStatus.bar)}
                  style={{ width: `${Math.min(previewUsage, 100)}%` }}
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                {formatUnits(used)} un em estoque neste local
                {typedCapacity < used && " — a capacidade está abaixo do estoque atual"}
              </p>
            </div>
          )}

          <div className="flex items-center justify-end gap-2.5 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="h-10 rounded-full border border-border bg-surface-container px-5 text-[13px] font-semibold text-foreground transition-colors hover:bg-surface-high disabled:opacity-60"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-primary px-6 text-[13px] font-semibold text-primary-foreground shadow-sm transition-all hover:bg-brand-dark active:scale-95 disabled:opacity-70"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Salvando..." : isEdit ? "Atualizar Local" : "Criar Local"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default StorageLocationFormModal;
